(() => {
  const normalise = (value = '') => value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  const escapeHtml = (value = '') => value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  }[character]));

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlight = (value, terms) => {
    if (!terms.length) return escapeHtml(value);
    const pattern = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'ig');
    return value.split(pattern).map((part) => (
      terms.some((term) => normalise(part) === term)
        ? `<mark>${escapeHtml(part)}</mark>`
        : escapeHtml(part)
    )).join('');
  };

  const distance = (a, b) => {
    if (Math.abs(a.length - b.length) > 1) return 2;
    const matrix = Array.from({ length: b.length + 1 }, (_, row) => [row]);
    for (let column = 0; column <= a.length; column += 1) matrix[0][column] = column;
    for (let row = 1; row <= b.length; row += 1) {
      for (let column = 1; column <= a.length; column += 1) {
        matrix[row][column] = Math.min(
          matrix[row - 1][column] + 1,
          matrix[row][column - 1] + 1,
          matrix[row - 1][column - 1] + (a[column - 1] === b[row - 1] ? 0 : 1)
        );
      }
    }
    return matrix[b.length][a.length];
  };

  const sendEvent = (name, detail) => {
    window.dispatchEvent(new CustomEvent(`liva7:${name}`, { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `liva7_${name}`, ...detail });
  };

  const initialiseSearch = () => {
    const form = document.querySelector('[data-l7hc-search-form]');
    if (!form) return;

    const input = form.querySelector('[data-l7hc-search-input]');
    const clear = form.querySelector('[data-l7hc-search-clear]');
    const status = form.querySelector('[data-l7hc-search-status]');
    const results = document.querySelector('[data-l7hc-search-results]');
    const resultsList = results?.querySelector('[data-l7hc-results-list]');
    const resultsSummary = results?.querySelector('[data-l7hc-results-summary]');
    const resultsEmpty = results?.querySelector('[data-l7hc-results-empty]');
    const index = document.querySelector('.l7hc-index');
    const popular = document.querySelector('.l7hc-popular');
    const cards = Array.from(document.querySelectorAll('[data-l7hc-card]'));
    const synonyms = {
      charge: ['billing', 'plan', 'subscription'],
      charges: ['billing', 'plan', 'subscription'],
      cost: ['billing', 'pricing', 'plan'],
      embed: ['placement', 'theme', 'block'],
      widget: ['placement', 'theme', 'block'],
      broken: ['troubleshoot', 'health', 'error'],
      missing: ['showing', 'placement', 'health'],
      menu: ['navigation', 'workspace'],
      report: ['performance', 'evidence', 'results']
    };
    let activeResults = [];
    let telemetryTimer;
    let lastTelemetry = '';

    const guides = cards.map((card) => {
      const category = card.closest('[data-l7hc-group]')?.querySelector('h2')?.textContent.trim() || 'Help Centre';
      const guide = {
        title: card.dataset.l7hcTitle || '',
        description: card.dataset.l7hcDescription || '',
        keywords: card.dataset.l7hcKeywords || '',
        content: card.dataset.l7hcContent || '',
        url: card.getAttribute('href'),
        category
      };
      guide.fields = {
        title: normalise(guide.title),
        description: normalise(guide.description),
        keywords: normalise(guide.keywords),
        content: normalise(guide.content)
      };
      guide.tokens = normalise(`${guide.title} ${guide.keywords}`).split(' ');
      return guide;
    });

    const expandedTerms = (query) => {
      const original = normalise(query).split(' ').filter(Boolean);
      return original.map((term) => ({
        term,
        alternatives: [term, ...(synonyms[term] || [])]
      }));
    };

    const scoreGuide = (guide, query, groups) => {
      const fullQuery = normalise(query);
      let score = 0;
      let matchedAll = true;

      if (guide.fields.title === fullQuery) score += 180;
      else if (guide.fields.title.includes(fullQuery)) score += 110;
      if (guide.fields.keywords.includes(fullQuery)) score += 60;
      if (guide.fields.description.includes(fullQuery)) score += 35;
      if (guide.fields.content.includes(fullQuery)) score += 18;

      groups.forEach(({ term, alternatives }) => {
        let termScore = 0;
        alternatives.forEach((candidate, candidateIndex) => {
          const weight = candidateIndex === 0 ? 1 : 0.72;
          if (guide.fields.title.includes(candidate)) termScore = Math.max(termScore, 36 * weight);
          if (guide.fields.keywords.includes(candidate)) termScore = Math.max(termScore, 28 * weight);
          if (guide.fields.description.includes(candidate)) termScore = Math.max(termScore, 18 * weight);
          if (guide.fields.content.includes(candidate)) termScore = Math.max(termScore, 8 * weight);
        });
        if (!termScore && term.length >= 5 && guide.tokens.some((token) => distance(term, token) <= 1)) termScore = 9;
        if (!termScore) matchedAll = false;
        score += termScore;
      });

      return matchedAll ? score : 0;
    };

    const makeSnippet = (guide, terms) => {
      const source = (guide.content || guide.description).replace(/\s+/g, ' ').trim();
      const normalisedSource = normalise(source);
      const positions = terms.map((term) => normalisedSource.indexOf(term)).filter((position) => position >= 0);
      const start = positions.length ? Math.max(0, Math.min(...positions) - 68) : 0;
      const clipped = source.slice(start, start + 190).trim();
      return `${start > 0 ? '…' : ''}${clipped}${start + 190 < source.length ? '…' : ''}`;
    };

    const updateUrl = (query) => {
      const url = new URL(window.location.href);
      if (query) url.searchParams.set('q', query);
      else url.searchParams.delete('q');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const render = () => {
      const query = input.value.trim();
      const groups = expandedTerms(query);
      const terms = groups.map(({ term }) => term);
      activeResults = query
        ? guides
          .map((guide) => ({ guide, score: scoreGuide(guide, query, groups) }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score || a.guide.title.localeCompare(b.guide.title))
          .slice(0, 8)
        : [];

      clear.hidden = !query;
      results.hidden = !query;
      index.hidden = Boolean(query);
      popular.hidden = Boolean(query);
      resultsList.innerHTML = activeResults.map(({ guide }) => `
        <a class="l7hc-search-result" href="${escapeHtml(guide.url)}">
          <span>${escapeHtml(guide.category)}</span>
          <strong>${highlight(guide.title, terms)}</strong>
          <small>${highlight(makeSnippet(guide, terms), terms)}</small>
          <b aria-hidden="true">→</b>
        </a>
      `).join('');
      resultsEmpty.hidden = activeResults.length !== 0;
      resultsSummary.textContent = activeResults.length
        ? `${activeResults.length} best ${activeResults.length === 1 ? 'match' : 'matches'} for “${query}”`
        : `No exact match for “${query}”`;
      status.textContent = query
        ? activeResults.length
          ? `${activeResults.length} ${activeResults.length === 1 ? 'guide' : 'guides'} found. Press Enter to open the first result.`
          : 'No guides found. Try a shorter phrase or use a common route below.'
        : `${guides.length} current guides`;
      updateUrl(query);

      window.clearTimeout(telemetryTimer);
      telemetryTimer = window.setTimeout(() => {
        const signature = `${normalise(query)}:${activeResults.length}`;
        if (query && signature !== lastTelemetry) {
          lastTelemetry = signature;
          sendEvent(activeResults.length ? 'help_search' : 'help_search_zero_results', {
            query: normalise(query),
            result_count: activeResults.length
          });
        }
      }, 450);
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (activeResults[0]) window.location.assign(activeResults[0].guide.url);
    });
    input.addEventListener('input', render);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && input.value) {
        input.value = '';
        render();
      }
      if (event.key === 'ArrowDown') {
        const firstResult = resultsList.querySelector('a');
        if (firstResult) {
          event.preventDefault();
          firstResult.focus();
        }
      }
    });
    resultsList.addEventListener('keydown', (event) => {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const links = Array.from(resultsList.querySelectorAll('a'));
      const current = links.indexOf(document.activeElement);
      if (current < 0) return;
      event.preventDefault();
      links[(current + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length].focus();
    });
    clear.addEventListener('click', () => {
      input.value = '';
      render();
      input.focus();
    });

    const initialQuery = new URL(window.location.href).searchParams.get('q');
    if (initialQuery) input.value = initialQuery;
    render();
  };

  const initialiseArticle = () => {
    const body = document.querySelector('[data-l7hc-article-body]');
    if (!body) return;
    const headings = Array.from(body.querySelectorAll('h2'));
    const tocLists = Array.from(document.querySelectorAll('[data-l7hc-toc]'));

    headings.forEach((heading, index) => {
      if (!heading.id) heading.id = `section-${index + 1}`;
    });
    const tocMarkup = headings.map((heading) => (
      `<li><a href="#${escapeHtml(heading.id)}">${escapeHtml(heading.textContent.trim())}</a></li>`
    )).join('');
    tocLists.forEach((list) => { list.innerHTML = tocMarkup; });

    document.querySelectorAll('[data-l7hc-feedback-answer]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-l7hc-feedback]');
        const answer = button.dataset.l7hcFeedbackAnswer;
        const article = panel.dataset.l7hcArticle;
        panel.querySelectorAll('[data-l7hc-feedback-answer]').forEach((choice) => {
          choice.setAttribute('aria-pressed', String(choice === button));
        });
        panel.querySelector('[data-l7hc-feedback-status]').textContent = answer === 'yes'
          ? 'Thanks. Your choice is saved on this device.'
          : 'Choose what was missing. Use the support link if you want to send it to the team.';
        panel.querySelector('[data-l7hc-feedback-more]').hidden = answer !== 'no';
        try { window.localStorage.setItem(`liva7-help-feedback:${article}`, answer); } catch (error) { /* Storage is optional. */ }
        sendEvent('help_article_feedback', { article, answer });
      });
    });

    document.querySelectorAll('[data-l7hc-feedback-reason]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-l7hc-feedback]');
        const article = panel.dataset.l7hcArticle;
        const reason = button.dataset.l7hcFeedbackReason;
        panel.querySelectorAll('[data-l7hc-feedback-reason]').forEach((choice) => {
          choice.setAttribute('aria-pressed', String(choice === button));
        });
        panel.querySelector('[data-l7hc-feedback-status]').textContent = 'Selected. Use the support link below to send it to the team.';
        sendEvent('help_article_feedback_reason', { article, reason });
      });
    });
  };

  initialiseSearch();
  initialiseArticle();
})();
